(() => {
  class StoreTimeline extends HTMLElement {
    constructor() {
      super();
      /** @type {HTMLTableRowElement[]} */
      this.rows = [];
      /** @type {MutationObserver[]} */
      this.rowObservers = [];
      /** @type {HTMLTableRowElement | null} */
      this.activeRow = null;
      /** @type {HTMLImageElement | null} */
      this.imageTarget = null;
      /** @type {HTMLElement | null} */
      this.placeholderEl = null;
      /** @type {string} */
      this.defaultImage = '';
      /** @type {string} */
      this.defaultAlt = '';
      /** @type {string} */
      this.placeholderSrc = '';
      this.isSyncing = false;
    }

    connectedCallback() {
      /** @type {HTMLTableRowElement[]} */
      this.rows = Array.from(this.querySelectorAll('[data-store-image]'));
      if (!this.rows.length) return;

      /** @type {HTMLImageElement | null} */
      this.imageTarget = this.querySelector('[data-store-image-target]');
      /** @type {HTMLElement | null} */
      this.placeholderEl = this.querySelector('[data-store-image-placeholder]');
      /** @type {string} */
      this.defaultImage = this.dataset.defaultImage || '';
      /** @type {string} */
      this.defaultAlt = this.dataset.defaultAlt || '';
      /** @type {string} */
      this.placeholderSrc = this.dataset.placeholderImage || '';
      this.isSyncing = false;

      this.disconnectObservers();
      this.rowObservers = [];

      this.ensureImageTarget();

      this.rows.forEach(row => {
        this.observeRow(row);
      });

      const activeRow = this.rows.find(row => row.classList.contains('active'));
      if (activeRow) {
        this.syncFromExternal(activeRow);
      } else if (this.rows[0]) {
        this.activateRow(this.rows[0]);
      }
    }

    /** @param {HTMLTableRowElement} row */
    observeRow(row) {
      if (!window.MutationObserver) return;
      const observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (mutation.attributeName === 'class') {
            const { target } = mutation;
            if (target instanceof HTMLTableRowElement) {
              this.onClassChange(target);
            }
          }
        });
      });
      observer.observe(row, { attributes: true, attributeFilter: ['class'] });
      this.rowObservers.push(observer);
    }

    /** @param {HTMLTableRowElement} row */
    onClassChange(row) {
      if (this.isSyncing) return;
      if (row.classList.contains('active')) {
        this.syncFromExternal(row);
      }
    }

    /** @param {HTMLTableRowElement} row */
    syncFromExternal(row) {
      this.isSyncing = true;
      this.rows.forEach(currentRow => {
        if (currentRow !== row) {
          currentRow.classList.remove('active');
        }
      });
      this.isSyncing = false;
      if (this.activeRow === row) return;
      this.activeRow = row;
      this.updateImage(row);
    }

    /** @param {HTMLTableRowElement} row */
    activateRow(row) {
      if (this.activeRow === row) return;
      this.isSyncing = true;
      this.rows.forEach(currentRow => {
        if (currentRow === row) {
          currentRow.classList.add('active');
        } else {
          currentRow.classList.remove('active');
        }
      });
      this.isSyncing = false;
      this.activeRow = row;
      this.updateImage(row);
    }

    ensureImageTarget() {
      if (this.imageTarget) return;
      const wrapper = this.querySelector('[data-store-image-wrapper]');
      if (!wrapper) return;
      const img = document.createElement('img');
      img.setAttribute('data-store-image-target', '');
      img.loading = 'lazy';
      img.width = 305;
      img.height = 380;
      img.classList.add('is-hidden');
      wrapper.insertAdjacentElement('afterbegin', img);
      this.imageTarget = img;
    }

    /** @param {HTMLTableRowElement} row */
    updateImage(row) {
      this.ensureImageTarget();
      if (!this.imageTarget) return;
      const imageSrc = row.dataset.storeImage || this.defaultImage;
      const altText = row.dataset.storeTitle || this.defaultAlt;
      if (imageSrc) {
        this.imageTarget.src = imageSrc;
        this.imageTarget.alt = altText;
        this.imageTarget.classList.remove('is-hidden');
        if (this.placeholderEl) {
          this.placeholderEl.classList.add('is-hidden');
        }
      } else {
        this.imageTarget.classList.add('is-hidden');
        if (this.placeholderEl) {
          this.placeholderEl.classList.remove('is-hidden');
        }
        if (this.placeholderSrc) {
          this.imageTarget.src = this.placeholderSrc;
          this.imageTarget.alt = altText;
        }
      }
    }

    disconnectedCallback() {
      this.disconnectObservers();
    }

    disconnectObservers() {
      if (!this.rowObservers || !this.rowObservers.length) return;
      this.rowObservers.forEach(observer => observer.disconnect());
      this.rowObservers = [];
    }
  }

  if (!window.customElements.get('store-timeline')) {
    window.customElements.define('store-timeline', StoreTimeline);
  }
})();
